// studioSlash — the Studio's own rows in the one Create menu (pen "Studio ·
// Create menu", spec §5; CreateMenu.tsx): Ask here, Create from a source…,
// then under Insert: Module, Page, Quiz, Question, Note, Document. The same
// rows open from "/" at the caret (beside the editor's heading chips) and from
// "+ Create"; a pick goes the same way from both (useStudioCreate).
// Question and Note are the editor's own blocks, put in the text at the
// caret: there is no part of those kinds, so "+ Create" (no caret) leaves
// them out. A Note is text only the author and the assistant see.
// Not a chord. Pure: the localised rows, and a pick read back by its place
// in this list.

export type { StudioSlashKey };
export { studioCreateItems, studioSlashItems, studioSlashKeyAt };

import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
    blockSlashItem,
    type SlashItem,
} from "../Editor/SlashMenu/slashItems.js";
import type { CreateKind } from "./studioCreate.js";

type StudioSlashKey =
    | { readonly type: "ask_here" }
    | { readonly type: "from_source" }
    | { readonly type: "insert"; readonly kind: CreateKind };

// The rows, in the menu's order (a row's place is its key).
type Row =
    | {
          readonly type: "studio";
          readonly key: StudioSlashKey;
          readonly label: MessageDescriptor;
          readonly hint: MessageDescriptor;
          // The line under it from "+ Create", where there is no "here"
          // (absent: the same as at the caret).
          readonly buttonHint?: MessageDescriptor;
          readonly icon: SlashItem["icon"];
          readonly keywords: readonly string[];
      }
    // One of the editor's own blocks, at the caret only.
    | { readonly type: "block"; readonly kind: "mcq" | "note" };

const ROWS: readonly Row[] = [
    {
        type: "studio",
        key: { type: "ask_here" },
        label: msg`Ask here`,
        hint: msg`Say what to make or change, right here`,
        buttonHint: msg`Say what to make or change`,
        icon: "ask",
        keywords: ["ask", "make", "write"],
    },
    {
        type: "studio",
        key: { type: "from_source" },
        label: msg`Create from a source…`,
        hint: msg`Pick a source, then what to make`,
        icon: "file-search",
        keywords: ["source", "from"],
    },
    {
        type: "studio",
        key: { type: "insert", kind: "module" },
        label: msg`Module`,
        hint: msg`Insert a module here`,
        icon: "folder",
        keywords: ["module", "folder"],
    },
    {
        type: "studio",
        key: { type: "insert", kind: "page" },
        label: msg`Page`,
        hint: msg`Insert a page here`,
        icon: "book-open",
        keywords: ["page", "lesson"],
    },
    {
        type: "studio",
        key: { type: "insert", kind: "quiz" },
        label: msg`Quiz`,
        hint: msg`Insert a quiz here`,
        icon: "mcq",
        keywords: ["quiz", "questions"],
    },
    { type: "block", kind: "mcq" },
    { type: "block", kind: "note" },
    {
        type: "studio",
        key: { type: "insert", kind: "document" },
        label: msg`Document`,
        hint: msg`Insert a document here`,
        icon: "file-text",
        keywords: ["document", "handout"],
    },
];

// A Studio row as a "/" row: its place in ROWS comes back on a pick.
const itemOf =
    (i18n: I18n, opening: "button" | "caret") =>
    (row: Row, at: number): readonly SlashItem[] => {
        switch (row.type) {
            case "block":
                return [];
            case "studio":
                return [
                    {
                        intent: {
                            type: "surface",
                            at,
                            group: row.key.type === "insert" ? "insert" : "top",
                            // Create from a source… opens its next step.
                            more: row.key.type === "from_source",
                            // Ask here takes the words typed after "/".
                            asks: row.key.type === "ask_here",
                        },
                        label: i18n._(row.label),
                        name: i18n._(row.label),
                        hint: i18n._(
                            opening === "button"
                                ? (row.buttonHint ?? row.hint)
                                : row.hint,
                        ),
                        icon: row.icon,
                        keywords: row.keywords,
                    },
                ];
            default: {
                const exhaustive: never = row;
                return exhaustive;
            }
        }
    };

// The rows at the caret: Question and Note are the editor's own blocks.
const studioSlashItems = (i18n: I18n): readonly SlashItem[] =>
    ROWS.flatMap((row, at) =>
        row.type === "block"
            ? [blockSlashItem(row.kind, i18n)]
            : itemOf(i18n, "caret")(row, at),
    );

// The rows from "+ Create" (no caret: no Question, no Note).
const studioCreateItems = (i18n: I18n): readonly SlashItem[] =>
    ROWS.flatMap(itemOf(i18n, "button"));

// The pick at a place in this list (absent: no such row, or an editor block).
const studioSlashKeyAt = (at: number): StudioSlashKey | undefined => {
    const row = ROWS[at];
    return row?.type === "studio" ? row.key : undefined;
};
